<template>
  <div v-if="formData">
    <CRow class="title pb-2">
      <CCol col="auto pl-0">
        <h2>
          {{ formData.property.name }}
        </h2>
      </CCol>
      <CCol col="auto pr-0">
        <div v-if="editPostingData">
          <CButton
            shape="pill"
            variant="outline"
            @click="showEdit = true"
          >
            Update Information
          </CButton>
        </div>
        <div v-else>
          <RChangeRequestButton :before-open="setContactSupportFlag" />
        </div>
      </CCol>
    </CRow>
    <RSpinnerOverlay
      :loading="loading"
      :opacity="0.7"
    >
      <CRow class="pt-4">
        <CCol
          md="12"
          lg="6"
          class="mb-4"
        >
          <CCard>
            <CCardHeader>
              Custom Titles
            </CCardHeader>
            <CCardBody>
              <div>
                <label>Behavior</label>
                <p>
                  {{ parseTitleSetting( formData.title_setting ) }}
                </p>
              </div>
              <PostingDetailMultiField
                :field="formData.title"
                label="Titles"
              />
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader>
              Ad Body
            </CCardHeader>
            <CCardBody>
              <PostingDetailMultiField
                :field="formData.romance_text"
                label="Romance Text"
              />
            </CCardBody>
          </CCard>

          <CCard>
            <CCardHeader>
              Headers and Footers
              <CIcon
                v-c-tooltip="{
                  content: 'If headers/footers/disclaimers have multiple entries, PostEngine will cycle through them, using only one for each post.',
                  boundaries: 'window'
                }"
                name="md-info"
                class="info-icon"
              />
            </CCardHeader>
            <CCardBody>
              <PostingDetailMultiField
                :field="formData.disclaimer"
                label="Disclaimer(s)"
              />
              <PostingDetailMultiField
                :field="formData.header"
                label="Header(s)"
              />
              <PostingDetailMultiField
                :field="formData.footer"
                label="Footer(s)"
              />
            </CCardBody>
          </CCard>

          <CCard>
            <CCardHeader>
              Location
            </CCardHeader>
            <CCardBody>
              <div class="d-flex justify-content-between">
                <label>Region</label>
                <p class="capitalize">
                  {{ formData.major_region ? getRegionById(formData.major_region).name : '(none)' }}
                </p>
              </div>
              <div class="d-flex justify-content-between">
                <label>Sub Region</label>
                <p class="capitalize">
                  {{ formData.sub_region ? getSubRegionById(formData.sub_region).name : '(none)' }}
                </p>
              </div>
              <PostingDetailMultiField
                :field="formData.location"
                label="Location(s)"
              />
            </CCardBody>
          </CCard>
        </CCol>
        <CCol
          md="12"
          lg="6"
          class="mb-4"
        >
          <CCard>
            <CCardHeader>
              Amenities
            </CCardHeader>
            <CCardBody>
              <PostingDetailMultiField
                :field="formData.amenities_property"
                label="Property Amenities"
              />
              <PostingDetailMultiField
                :field="formData.amenities_unit"
                label="Unit Amenities"
              />
              <PostingDetailAmenities :property="formData" />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </RSpinnerOverlay>
    <PostingDetailEdit
      :show.sync="showEdit"
      :property="formData"
      @update:property="fetchProperty"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { RChangeRequestButton } from '@/components/buttons'
import { RSpinnerOverlay } from '@/components/spinners'
import { CraigslistAPI, RooofAccountAPI } from '@/services/api/resources'

import PostingDetailAmenities from './_components/PostingDetailAmenities'
import PostingDetailEdit from './_components/PostingDetailEdit'
import PostingDetailMultiField from './_components/PostingDetailMultiField'

export default {
  name: 'PostingDetail',
  components: {
    RChangeRequestButton,
    RSpinnerOverlay,
    PostingDetailMultiField,
    PostingDetailAmenities,
    PostingDetailEdit
  },
  data () {
    return {
      loading: false,
      permissions: [],
      peGroups: [],
      editPostingData: false,
      showEdit: false,
      formData: null
    }
  },
  computed: {
    ...mapGetters({
      account: 'auth/account',
      getRegionById: 'craigslist/getRegionById',
      getSubRegionById: 'craigslist/getSubRegionById'
    })
  },
  created () {
    this.fetchProperty()
  },
  methods: {
    /**
     * Fetch property
     *
     */
    async fetchProperty () {
      const id = this.$route.params.pid
      this.loading = true
      const clProperty = await CraigslistAPI.properties.retrieve({ id })
      if (clProperty) {
        this.formData = clProperty.data
        this.peGroups = clProperty.data.property.postengine_groups
      }
      this.fetchPermissions()
      this.loading = false
    },
    /**
     * Fetch user edit permissions
     * There is property level and postengine group level permissions a user can have.
     * Staff are able to edit without explicit permissions.
     *
     */
    async fetchPermissions () {
      const response = await RooofAccountAPI.users.permissions({ id: this.account.id })
      this.permissions = response.data

      const propertyPermissions = this.permissions.find(permission => {
        return permission.object_pk === this.$route.params.pid &&
        permission.content_type.model === 'craigslistproperty' &&
        permission.permission.codename === 'edit_posting_data'
      })

      // check if user has permission to edit for a postengine group the property belongs to
      const groupPermissions = this.permissions.find(permission => {
        return this.peGroups.includes(parseInt(permission.object_pk)) &&
        permission.content_type.model === 'postenginegroup' &&
        permission.permission.codename === 'edit_posting_data'
      })

      if (propertyPermissions || groupPermissions || this.$store.getters['auth/isStaff']) {
        this.editPostingData = true
      }
    },
    /**
     * Get custom title setting choices to be displayed.
     *
     * @return {Array}
     */
    parseTitleSetting (setting) {
      switch (setting) {
        case 'no_titles':
          return 'Let PostEngine auto-generate titles'
        case 'alternate_titles':
          return 'Randomly pick between stored and auto-generated titles'
        case 'stored_only':
          return 'Randomly pick from stored titles only'
        case 'user_choose':
          return 'Let me pick a custom title every time'
        default:
          return 'Unknown'
      }
    },
    /**
     * Sets the "My company/property information is incorrect" checkbox
     * in the Contact Support modal.
     */
    setContactSupportFlag () {
      this.$store.commit('support/set', ['incorrect_company_property_info', true])
    }
  }
}
</script>

<style lang="scss" scoped>
.title {
  margin: auto;
  border-bottom: 2px solid $gray-100;
  justify-content: space-between;
  align-items: center;
}
.card {
  .card-header {
    font-weight: 800;
    font-size: 1.1rem;
    display: flex;
    align-items: center;

    .info-icon {
      width: 15px;
      margin-left: 4px;
    }
  }
  ::v-deep .card-body {
    label {
      font-size: 1rem;
      font-weight: bold;
    }
    .capitalize {
      text-transform: capitalize;
    }

    .posting-detail-field:last-of-type p {
      margin-bottom: 0;
    }
  }
}
</style>
